<template>
    <Head title="Incident Types - Settings" />

    <div class="py-6 px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="sm:flex sm:items-center sm:justify-between mb-6">
            <div class="flex items-center gap-3">
                <div>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Incident Types</h1>
                    <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">
                        Manage incident types, severities, and SLA requirements
                    </p>
                </div>
                <InfoButton title="Managing Incident Types" variant="default">
                    <p>
                        <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                        The Incident Types page is where you define and manage the classification categories used
                        throughout your incident management system. Incident types help organize incidents by their
                        nature (Safety, Security, Service & Operations, Environmental, HR & Compliance, etc.), enable
                        proper routing and assignment, facilitate trend analysis and reporting, and ensure appropriate
                        SLA policies are applied. Well-defined incident types are fundamental to effective incident
                        management as they provide structure, consistency, and meaningful data for continuous improvement.
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                        • <strong>Search:</strong> Quickly find incident types by name or description<br>
                        • <strong>Category Filter:</strong> View types by category (Safety, Health, Environmental,
                          Security, Operational, Quality, Transportation, Compliance, Workplace, Contractor, Visitor,
                          General)<br>
                        • <strong>Status Filter:</strong> Toggle between viewing All Types, Active Only, or Inactive Only<br>
                        • <strong>Add Incident Type:</strong> Click to create a new incident classification with name,
                          description, category, default severity/priority, and SLA policy association<br>
                        • <strong>Table View:</strong> See all incident types with their category, default severity,
                          default priority, active/inactive status, and action buttons<br>
                        • <strong>Edit:</strong> Click the edit icon to modify an existing incident type's configuration<br>
                        • <strong>Delete:</strong> Remove incident types that are no longer needed (only if not in use
                          by existing incidents)<br>
                        • <strong>Toggle Status:</strong> Activate or deactivate incident types - inactive types won't
                          appear in incident reporting forms but preserve historical data
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                        • <strong>Use meaningful names</strong> - Incident types should be clear and easily understood
                          by all users<br>
                        • <strong>Align with industry standards</strong> - Consider OSHA, ISO, or other relevant
                          frameworks when defining types<br>
                        • <strong>Balance specificity</strong> - Too many types create confusion, too few limit analysis.
                          Aim for 15-30 types<br>
                        • <strong>Use categories consistently</strong> - Proper categorization enables better filtering
                          and reporting<br>
                        • <strong>Set appropriate defaults</strong> - Default severity and priority help users but can
                          be overridden during triage<br>
                        • <strong>Associate SLA policies</strong> - Link incident types to SLA policies to ensure timely
                          response requirements<br>
                        • <strong>Deactivate instead of delete</strong> - Preserves historical data while hiding types
                          from new reports<br>
                        • <strong>Review regularly</strong> - As your organization evolves, update incident types to
                          match current needs<br>
                        • <strong>Consider reporting needs</strong> - Structure types to support the analysis and
                          reporting your organization requires<br>
                        • Changes to incident types don't affect existing incidents - they retain their original type
                          classification
                    </p>
                </InfoButton>
            </div>
            <div class="mt-4 sm:mt-0">
                <Link
                    :href="route('settings.incidents.types.create')"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Incident Type
                </Link>
            </div>
        </div>

        <!-- Filters -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg mb-6 border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <!-- Search -->
                    <div>
                        <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Search</label>
                        <input
                            id="search"
                            v-model="search"
                            type="text"
                            placeholder="Search incident types..."
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>

                    <!-- Category Filter -->
                    <div>
                        <label for="category" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                        <select
                            id="category"
                            v-model="categoryFilter"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                            <option value="">All Categories</option>
                            <option value="Safety">Safety</option>
                            <option value="Health">Health</option>
                            <option value="Environmental">Environmental</option>
                            <option value="Security">Security</option>
                            <option value="Operational">Operational</option>
                            <option value="Quality">Quality</option>
                            <option value="Transportation">Transportation</option>
                            <option value="Compliance">Compliance</option>
                            <option value="Workplace">Workplace</option>
                            <option value="Contractor">Contractor</option>
                            <option value="Visitor">Visitor</option>
                            <option value="General">General</option>
                        </select>
                    </div>

                    <!-- Status Filter -->
                    <div>
                        <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                        <select
                            id="status"
                            v-model="activeFilter"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                            <option value="all">All Types</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <!-- Types Table -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Name & Category
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Default Severity
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Default Priority
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                        </th>
                        <th scope="col" class="relative px-6 py-3">
                            <span class="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr v-if="filteredTypes.length === 0">
                        <td colspan="5" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                            No incident types found
                        </td>
                    </tr>
                    <tr v-for="type in filteredTypes" :key="type.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td class="px-6 py-4">
                            <div class="flex items-center">
                                <div :class="`flex-shrink-0 h-10 w-10 rounded-full bg-${type.color}-100 flex items-center justify-center`">
                                    <span class="text-lg">{{ getIconForType(type.icon) }}</span>
                                </div>
                                <div class="ml-4">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ type.name }}</div>
                                    <div class="text-sm text-gray-500 dark:text-gray-400">{{ type.category }}</div>
                                </div>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span :class="getSeverityClass(type.default_severity)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                                {{ type.default_severity }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span :class="getPriorityClass(type.default_priority)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                                {{ type.default_priority }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span v-if="type.is_active" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                Active
                            </span>
                            <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                Inactive
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                                :href="route('settings.incidents.types.edit', type.id)"
                                class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-4"
                            >
                                Edit
                            </Link>
                            <button
                                @click="toggleStatus(type.id)"
                                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mr-4"
                            >
                                {{ type.is_active ? 'Deactivate' : 'Activate' }}
                            </button>
                            <button
                                @click="confirmDelete(type)"
                                class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div v-if="props.types.data && props.types.data.length > 0" class="mt-6">
            <Pagination :links="props.types.links" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import Pagination from '@/Components/Pagination.vue';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    types: {
        type: Object,
        required: true,
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
    categories: {
        type: Array,
        default: () => [],
    },
});

const search = ref(props.filters.search || '');
const categoryFilter = ref(props.filters.category || '');
const activeFilter = ref(props.filters.active !== undefined ? (props.filters.active ? 'active' : 'inactive') : 'all');

const filteredTypes = computed(() => {
    const typesList = props.types.data || [];
    return typesList.filter(type => {
        // Search filter
        if (search.value && !type.name.toLowerCase().includes(search.value.toLowerCase()) &&
            !type.description?.toLowerCase().includes(search.value.toLowerCase())) {
            return false;
        }

        // Category filter
        if (categoryFilter.value && type.category !== categoryFilter.value) {
            return false;
        }

        // Status filter
        if (activeFilter.value === 'active' && !type.is_active) return false;
        if (activeFilter.value === 'inactive' && type.is_active) return false;

        return true;
    });
});

const getIconForType = (iconName) => {
    // Simple icon mapping - in production, you'd use a proper icon library
    const icons = {
        'alert-triangle': '⚠️',
        'tool': '🔧',
        'flame': '🔥',
        'droplet': '💧',
        'zap': '⚡',
        'box': '📦',
        'arrow-up': '⬆️',
        'heart': '❤️',
        'cloud': '☁️',
        'trash-2': '🗑️',
        'lock': '🔒',
        'shield-off': '🛡️',
        'database': '💾',
        'activity': '📊',
        'pause-circle': '⏸️',
        'check-circle': '✅',
        'alert-octagon': '🛑',
        'truck': '🚚',
        'package': '📦',
        'file-text': '📄',
        'clipboard': '📋',
        'alert-circle': '⚠️',
        'users': '👥',
        'home': '🏠',
        'briefcase': '💼',
        'user-check': '✓',
        'target': '🎯',
        'more-horizontal': '⋯',
    };
    return icons[iconName] || '📌';
};

const getSeverityClass = (severity) => {
    const classes = {
        low: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
        medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        critical: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[severity] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        normal: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
};

const toggleStatus = (typeId) => {
    router.post(route('settings.incidents.types.toggle', typeId), {}, {
        preserveScroll: true,
    });
};

const confirmDelete = (type) => {
    if (confirm(`Are you sure you want to delete "${type.name}"? This action cannot be undone.`)) {
        router.delete(route('settings.incidents.types.destroy', type.id), {
            preserveScroll: true,
        });
    }
};
</script>
